import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Droplets, Sun, Filter, CloudRain, Home as HomeIcon, Sprout, Zap,
  Leaf, Recycle, FlaskConical,
  CheckCircle, Users, Award, Globe,
  ArrowRight, Quote, ChevronLeft, ChevronRight,
  Wrench, HandCoins, Hammer, ShieldCheck, Sparkles, Network, Target
} from 'lucide-react';
import { useT, RichText } from '../i18n/I18nContext';

const HERO_IMAGE = "https://images.unsplash.com/photo-1740741705746-74e6f9b93b4f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwY2hpbGRyZW4lMjBkcmlua2luZyUyMGNsZWFuJTIwd2F0ZXIlMjB0YXB8ZW58MHx8fHwxNzcyNzk4NDc2fDA&ixlib=rb-4.1.0&q=85";

// SMARTechs in action — original SMART Centre Cameroon photos (URLs only; captions come from i18n)
const carouselUrls = [
  "https://customer-assets.emergentagent.com/job_c4e4e0b2-a64a-4eeb-a271-9dd6b7a39c40/artifacts/5893nwhx_Ropepump-1170x500.jpg",
  "https://customer-assets.emergentagent.com/job_c4e4e0b2-a64a-4eeb-a271-9dd6b7a39c40/artifacts/ryzao0g6_WhatsApp%20Image%202026-03-06%20at%209.11.32%20PM%20%281%29.jpeg",
  "https://customer-assets.emergentagent.com/job_c4e4e0b2-a64a-4eeb-a271-9dd6b7a39c40/artifacts/8zgiknno_WhatsApp%20Image%202026-03-06%20at%209.11.33%20PM.jpeg",
  "https://customer-assets.emergentagent.com/job_c4e4e0b2-a64a-4eeb-a271-9dd6b7a39c40/artifacts/blqfqtoa_water%20oump.png",
  "https://customer-assets.emergentagent.com/job_c4e4e0b2-a64a-4eeb-a271-9dd6b7a39c40/artifacts/xz7za40j_Water%20filter.jpg",
  "https://customer-assets.emergentagent.com/job_c4e4e0b2-a64a-4eeb-a271-9dd6b7a39c40/artifacts/0u7kqgws_SCC%20CEO%20and%20Mr%20Hanks.jpeg",
  "https://customer-assets.emergentagent.com/job_c4e4e0b2-a64a-4eeb-a271-9dd6b7a39c40/artifacts/0xenrur5_event%20in%20Yao.jpeg",
  "https://customer-assets.emergentagent.com/job_wash-cameroon/artifacts/sk4ejcnc_WhatsApp%20Image%202026-03-06%20at%209.13.10%20PM%20%284%29.jpeg",
];

const smartechIcons = [Droplets, Sun, Filter, CloudRain, HomeIcon, Sprout, Zap, Recycle];
const trainingIcons = [
  { icon: Droplets, color: 'bg-blue-50 text-blue-600' },
  { icon: Leaf, color: 'bg-green-50 text-green-600' },
  { icon: Recycle, color: 'bg-amber-50 text-amber-600' },
  { icon: FlaskConical, color: 'bg-purple-50 text-purple-600' },
];
const benefitIcons = [CheckCircle, Users, Award, Globe];
const visionIcons = [Wrench, HandCoins, Hammer, HomeIcon, ShieldCheck, Network, Sparkles];

const galleryImages = [
  { url: "https://customer-assets.emergentagent.com/job_c4e4e0b2-a64a-4eeb-a271-9dd6b7a39c40/artifacts/0xenrur5_event%20in%20Yao.jpeg", alt: "SMART Centres booth at international event" },
  { url: "https://customer-assets.emergentagent.com/job_c4e4e0b2-a64a-4eeb-a271-9dd6b7a39c40/artifacts/0u7kqgws_SCC%20CEO%20and%20Mr%20Hanks.jpeg", alt: "SCC CEO with Mr Hanks at SMART Centre Group exhibition" },
  { url: "https://customer-assets.emergentagent.com/job_c4e4e0b2-a64a-4eeb-a271-9dd6b7a39c40/artifacts/ryzao0g6_WhatsApp%20Image%202026-03-06%20at%209.11.32%20PM%20%281%29.jpeg", alt: "Community-led borehole installation" },
  { url: "https://customer-assets.emergentagent.com/job_wash-cameroon/artifacts/sk4ejcnc_WhatsApp%20Image%202026-03-06%20at%209.13.10%20PM%20%284%29.jpeg", alt: "Completed community borehole" },
];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

function SmartechCarousel() {
  const { t } = useT();
  const [index, setIndex] = useState(0);
  const total = carouselUrls.length;
  const captions = t('home.carousel.captions') || [];

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % total), 4500);
    return () => clearInterval(id);
  }, [total]);

  const goPrev = () => setIndex((i) => (i - 1 + total) % total);
  const goNext = () => setIndex((i) => (i + 1) % total);

  return (
    <section className="py-10 md:py-14 bg-white" data-testid="smartech-carousel-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6 md:mb-8">
          <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('home.carousel.eyebrow')}</span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 font-heading">
            {t('home.carousel.title')}
          </h2>
        </div>

        <div className="relative rounded-3xl overflow-hidden bg-slate-50 border border-slate-200 shadow-sm" data-testid="smartech-carousel">
          <div className="relative w-full aspect-[16/9] md:aspect-[21/9] bg-slate-100 overflow-hidden">
            <AnimatePresence>
              <motion.img
                key={index}
                src={carouselUrls[index]}
                alt={captions[index] || ''}
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                data-testid={`carousel-slide-${index}`}
              />
            </AnimatePresence>

            <button
              type="button"
              onClick={goPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-primary rounded-full p-3 shadow-lg transition-all"
              aria-label={t('home.carousel.prev')}
              data-testid="carousel-prev"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-primary rounded-full p-3 shadow-lg transition-all"
              aria-label={t('home.carousel.next')}
              data-testid="carousel-next"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>

          <div className="px-6 py-5 border-t border-slate-100 bg-white">
            <p className="text-base md:text-lg text-slate-700 text-center font-medium" data-testid="carousel-caption">
              {captions[index]}
            </p>
            <div className="flex justify-center gap-2 mt-4">
              {carouselUrls.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIndex(i)}
                  className={`h-2 rounded-full transition-all ${i === index ? 'w-8 bg-secondary' : 'w-2 bg-slate-300 hover:bg-slate-400'}`}
                  aria-label={`Go to slide ${i + 1}`}
                  data-testid={`carousel-dot-${i}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SmartAcronymSection() {
  const { t } = useT();
  const smartLetters = [
    { letter: 'S', word: t('home.smart.S') },
    { letter: 'M', word: t('home.smart.M') },
    { letter: 'A', word: t('home.smart.A') },
    { letter: 'R', word: t('home.smart.R') },
    { letter: 'T', word: t('home.smart.T') },
  ];
  const visionPoints = (t('home.smart.points') || []).map((p, i) => ({ ...p, Icon: visionIcons[i] || Sparkles }));

  return (
    <section className="py-16 md:py-24 bg-accent" data-testid="smart-acronym-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('home.smart.eyebrow')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-6 font-heading">
              {t('home.smart.title')}
            </h2>
            <div className="space-y-3 mb-6">
              {smartLetters.map((item, i) => (
                <motion.div
                  key={item.letter}
                  className="flex items-center gap-4 bg-white rounded-xl p-3 border border-slate-100 shadow-sm"
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  data-testid={`smart-letter-${item.letter}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-primary text-white flex items-center justify-center font-bold text-2xl font-heading flex-shrink-0">
                    {item.letter}
                  </div>
                  <p className="text-lg font-semibold text-slate-800">{item.word}</p>
                </motion.div>
              ))}
            </div>
            <RichText text={t('home.smart.explanation')} className="text-slate-600 leading-relaxed" />
          </motion.div>

          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Target className="h-6 w-6 text-secondary" />
              <h3 className="text-2xl font-bold text-slate-900 font-heading">{t('home.smart.visionTitle')}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {visionPoints.map((point, i) => (
                <motion.div
                  key={point.title}
                  className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  data-testid={`vision-point-${i}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-secondary/10 text-secondary flex items-center justify-center mb-3">
                    <point.Icon className="h-5 w-5" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-1 font-heading text-base">{point.title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{point.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const { t } = useT();
  const smartechs = (t('home.smartechs.cards') || []).map((c, i) => ({ ...c, Icon: smartechIcons[i] || Droplets }));
  const trainingCategories = (t('home.trainings.categories') || []).map((c, i) => ({ ...c, ...trainingIcons[i] }));
  const benefits = (t('home.benefits.items') || []).map((b, i) => ({ ...b, Icon: benefitIcons[i] || CheckCircle }));
  const testimonials = t('home.testimonials.items') || [];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] md:min-h-[65vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Clean water access in Cameroon" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4 font-heading">
              {t('home.hero.title')}
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-3 font-heading font-medium">
              {t('home.hero.subtitle')}
            </p>
            <p className="text-sm sm:text-base text-blue-100 mb-6 leading-relaxed">
              {t('home.hero.description')}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/trainings"
                className="bg-secondary text-white hover:bg-secondary-hover rounded-full px-6 py-3 font-semibold transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 btn-secondary"
                data-testid="hero-enroll-btn"
              >
                {t('home.hero.enrollCta')}
              </Link>
              <Link
                to="/contact"
                className="bg-white text-primary hover:bg-blue-50 rounded-full px-6 py-3 font-semibold transition-all shadow-lg hover:shadow-xl hover:-translate-y-1"
                data-testid="hero-contact-btn"
              >
                {t('home.hero.contactCta')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SMARTechs Carousel */}
      <SmartechCarousel />

      {/* SMART acronym + vision */}
      <SmartAcronymSection />

      {/* About teaser */}
      <section className="py-16 md:py-24 bg-white" data-testid="about-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            {...fadeInUp}
            viewport={{ once: true }}
            whileInView="animate"
            initial="initial"
          >
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('home.about.eyebrow')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-4 mb-6 font-heading">
              {t('home.about.title')}
            </h2>
            <RichText text={t('home.about.p1')} className="block text-lg text-slate-600 leading-relaxed mb-4" />
            <RichText text={t('home.about.p2')} className="block text-lg text-slate-600 leading-relaxed mb-8" />
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-primary font-semibold hover:text-secondary transition-colors"
              data-testid="about-read-more"
            >
              {t('home.about.readMore')} <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* SMARTechs Section */}
      <section className="py-16 md:py-24 bg-accent" data-testid="smartechs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('home.smartechs.eyebrow')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-4 font-heading">
              {t('home.smartechs.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {smartechs.map((tech, index) => (
              <motion.div
                key={tech.title}
                className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-lg hover:border-secondary/30 transition-all card-hover group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`smartech-card-${index}`}
              >
                <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center mb-4 group-hover:bg-secondary transition-colors tech-icon">
                  <tech.Icon className="h-6 w-6 text-primary group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2 font-heading">{tech.title}</h3>
                <p className="text-sm text-slate-600">{tech.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/smartechs"
              className="inline-flex items-center gap-2 bg-primary text-white hover:bg-primary-hover rounded-full px-8 py-3 font-semibold transition-all shadow-md hover:shadow-lg btn-primary"
              data-testid="view-all-smartechs"
            >
              {t('home.smartechs.explore')} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Trainings Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="trainings-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('home.trainings.eyebrow')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-4 font-heading">
              {t('home.trainings.title')}
            </h2>
            <p className="text-lg text-slate-600 mt-4 max-w-2xl mx-auto">
              {t('home.trainings.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trainingCategories.map((category, index) => (
              <motion.div
                key={category.title}
                className="bg-white overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all border border-slate-100 flex flex-col h-full card-hover"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`training-category-${index}`}
              >
                <div className={`p-6 ${category.color}`}>
                  <category.icon className="h-10 w-10" />
                </div>
                <div className="p-6 flex-grow flex flex-col">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2 font-heading">{category.title}</h3>
                  <p className="text-sm text-slate-600 flex-grow">{category.description}</p>
                  <Link
                    to="/trainings"
                    className="inline-flex items-center gap-1 text-primary font-medium text-sm mt-4 hover:text-secondary transition-colors"
                  >
                    {t('home.trainings.viewCourses')} <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/trainings"
              className="inline-flex items-center gap-2 bg-secondary text-white hover:bg-secondary-hover rounded-full px-8 py-3 font-semibold transition-all shadow-md hover:shadow-lg btn-secondary"
              data-testid="view-all-trainings"
            >
              {t('home.trainings.viewAll')} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-primary" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('home.benefits.eyebrow')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 font-heading">
              {t('home.benefits.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`benefit-card-${index}`}
              >
                <benefit.Icon className="h-10 w-10 text-secondary mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2 font-heading">{benefit.title}</h3>
                <p className="text-sm text-blue-100">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Teaser */}
      <section className="py-16 md:py-24 bg-white" data-testid="gallery-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('home.gallery.eyebrow')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-4 font-heading">
              {t('home.gallery.title')}
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <motion.div
                key={index}
                className="aspect-square overflow-hidden rounded-xl img-zoom"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <img src={image.url} alt={image.alt} className="w-full h-full object-cover" />
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/impact"
              className="inline-flex items-center gap-2 border-2 border-primary text-primary hover:bg-accent rounded-full px-8 py-3 font-semibold transition-all"
              data-testid="view-gallery"
            >
              {t('home.gallery.cta')} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-accent" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">{t('home.testimonials.eyebrow')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-4 font-heading">
              {t('home.testimonials.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-md testimonial-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`testimonial-${index}`}
              >
                <Quote className="h-8 w-8 text-secondary/30 mb-4" />
                <p className="text-lg text-slate-700 mb-6 italic">“{testimonial.quote}”</p>
                <div>
                  <p className="font-semibold text-slate-900">{testimonial.author}</p>
                  <p className="text-sm text-slate-500">{testimonial.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-secondary" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">
            {t('home.cta.title')}
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            {t('home.cta.subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/trainings"
              className="bg-white text-secondary hover:bg-green-50 rounded-full px-8 py-4 font-semibold text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-1"
              data-testid="cta-enroll"
            >
              {t('home.cta.enroll')}
            </Link>
            <Link
              to="/contact"
              className="bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-4 font-semibold text-lg transition-all"
              data-testid="cta-contact"
            >
              {t('home.cta.contact')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
